/**
 * PostHog client initialization and core methods
 */
import posthog from "posthog-js";
import { getConfig } from "src/config/Config";

let posthogInitialized = false;

function flushPostHogEvents(): void {
  if (!posthogInitialized) return;
}

function setupUnloadHandlers(): void {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushPostHogEvents();
    }
  });
  window.addEventListener("beforeunload", () => {
    flushPostHogEvents();
  });
  window.addEventListener("pagehide", () => {
    flushPostHogEvents();
  });
}

export function initialize(): void {
  if (posthogInitialized) return;

  const config = getConfig();
  const apiKey = config.POSTHOG_API_KEY;
  const host = config.POSTHOG_HOST;

  if (!apiKey || !host) {
    console.warn("PostHog API key or host not configured. Analytics will not be initialized.");
    return;
  }

  try {
    posthog.init(apiKey, {
      api_host: host,
      autocapture: true,
      capture_pageview: false, // Disabled - we track pageviews manually via usePageTracking for SPA route changes
      capture_pageleave: true,
      loaded: (posthogInstance) => {
        if (process.env.REACT_APP_BUILD_ENV !== "production") {
          console.log("PostHog initialized successfully", posthogInstance);
        }
        setupUnloadHandlers();
      }
    });
    posthogInitialized = true;
  } catch (error) {
    console.error("Failed to initialize PostHog:", error);
  }
}

export function identifyUser(userId: string, properties?: Record<string, any>): void {
  if (!posthogInitialized) return;
  try {
    posthog.identify(userId, properties);
  } catch (error) {
    console.error("Failed to identify user in PostHog:", error);
  }
}

export function resetUser(): void {
  if (!posthogInitialized) return;
  try {
    posthog.reset();
  } catch (error) {
    console.error("Failed to reset user in PostHog:", error);
  }
}

export function trackEvent(eventName: string, properties?: Record<string, any>): void {
  if (!posthogInitialized) return;
  try {
    posthog.capture(eventName, properties);
  } catch (error) {
    console.error(`Failed to track event ${eventName} in PostHog:`, error);
  }
}

export function setUserProperties(properties: Record<string, any>): void {
  if (!posthogInitialized) return;
  try {
    posthog.setPersonProperties(properties);
  } catch (error) {
    console.error("Failed to set user properties in PostHog:", error);
  }
}
